import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Check, CheckCircle2, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { getAssessment, getJournal, saveJournalDay } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { BrandLockup } from "@/components/BrandLockup";
import { LegalFooter } from "@/components/LegalFooter";

const DAYS = [1, 2, 3, 4, 5];

const DayCard = ({ day, j, entry, primaryKey, onSave }) => {
  const saved = !!entry;
  const [count, setCount] = useState(entry?.count ?? "");
  const [note, setNote] = useState(entry?.note ?? "");
  const [intervened, setIntervened] = useState(entry?.intervened ?? false);
  const [busy, setBusy] = useState(false);

  const dirty =
    String(count) !== String(entry?.count ?? "") ||
    (note || "") !== (entry?.note ?? "") ||
    intervened !== (entry?.intervened ?? false);

  const submit = async () => {
    if (count === "" || Number(count) < 0) {
      toast.error(j.countLabel[primaryKey]);
      return;
    }
    setBusy(true);
    try {
      await onSave(day, { count: Number(count), note: note || null, intervened });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2DFD8]" data-testid={`journal-day-${day}`}>
      <div className="flex items-center justify-between px-6 md:px-8 py-4 border-b border-[#E2DFD8]">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56]">{j.dayOf(day)}</p>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-xs text-[#475F51]" data-testid={`journal-saved-${day}`}>
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} /> {j.saved}
          </span>
        )}
      </div>
      <div className="px-6 md:px-8 py-6 space-y-5">
        <div>
          <label className="block text-base text-[#0B0E14] font-medium mb-1">{j.countLabel[primaryKey]}</label>
          <p className="text-xs text-[#718096] mb-3">{j.countHint[primaryKey]}</p>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid={`journal-count-${day}`}
            className="w-28 border border-[#E2DFD8] bg-[#F6F5F2] px-4 py-3 text-lg tabular-nums text-[#0B0E14] focus:outline-none focus:border-[#A68A56] transition-colors"
          />
        </div>
        <textarea
          rows={2}
          value={note}
          maxLength={280}
          onChange={(e) => setNote(e.target.value)}
          placeholder={j.notePlaceholder}
          data-testid={`journal-note-${day}`}
          className="w-full border border-[#E2DFD8] bg-[#F6F5F2] px-4 py-3 text-sm text-[#0B0E14] focus:outline-none focus:border-[#A68A56] transition-colors resize-none"
        />
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid={`journal-intervened-label-${day}`}>
          <input
            type="checkbox"
            checked={intervened}
            onChange={(e) => setIntervened(e.target.checked)}
            data-testid={`journal-intervened-${day}`}
            className="w-4 h-4 accent-[#A68A56]"
          />
          <span className="text-sm text-[#2C363F]">{j.intervenedLabel}</span>
        </label>
        <button
          onClick={submit}
          disabled={busy || (saved && !dirty)}
          data-testid={`journal-save-${day}`}
          className="inline-flex items-center gap-2 bg-[#0B0E14] text-white px-6 py-3 text-sm font-medium disabled:opacity-40 hover:bg-[#1a2029] transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2} />}
          {saved ? j.update : j.save}
        </button>
      </div>
    </div>
  );
};

export default function Journal() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const j = t.journal;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [primaryKey, setPrimaryKey] = useState(null);
  const [protocol, setProtocol] = useState("");
  const [metric, setMetric] = useState("");
  const [entries, setEntries] = useState({});

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [assess, jr] = await Promise.all([getAssessment(id, lang), getJournal(id)]);
        if (!active) return;
        const key = assess.result.primary_bottleneck.key;
        setPrimaryKey(key);
        setProtocol(t.results.interventionPlaybook[key].protocol);
        setMetric(assess.result.success_metric.label);
        setEntries(jr.journal || {});
        setError(false);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, lang, t]);

  const handleSave = async (day, payload) => {
    await saveJournalDay(id, { day, ...payload });
    setEntries((prev) => ({ ...prev, [day]: { ...payload, note: payload.note } }));
    toast.success(j.saved);
  };

  const loggedDays = DAYS.filter((d) => entries[String(d)] || entries[d]);
  const loggedCount = loggedDays.length;
  const complete = loggedCount === 5;
  const getEntry = (d) => entries[String(d)] || entries[d] || null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#A68A56]" />
      </div>
    );
  }

  if (error || !primaryKey) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex flex-col items-center justify-center px-6 text-center" data-testid="journal-error">
        <p className="text-[#4A5568] mb-6">{j.loadError}</p>
        <Link to="/" className="text-sm text-[#0B0E14] underline underline-offset-2">{t.footer.back}</Link>
      </div>
    );
  }

  const firstCount = getEntry(loggedDays[0])?.count ?? 0;
  const lastCount = getEntry(loggedDays[loggedDays.length - 1])?.count ?? 0;
  const interventions = DAYS.filter((d) => getEntry(d)?.intervened).length;

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex flex-col" data-testid="journal-page">
      <header className="border-b border-[#E2DFD8] bg-[#F6F5F2]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <span data-testid="brand-header"><BrandLockup /></span>
          <LanguageToggle />
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 md:px-12 py-12 md:py-16">
        <Link
          to={`/results/${id}`}
          data-testid="back-to-results"
          className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0B0E14] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          {j.backToResults}
        </Link>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-3">{j.overline}</p>
        <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight text-[#0B0E14]">{j.title}</h1>
        <p className="mt-2 text-sm text-[#718096]">{j.protocolLabel}: <span className="text-[#4A5568]">{protocol}</span></p>
        <p className="mt-6 text-base text-[#4A5568] leading-relaxed max-w-2xl">{j.intro}</p>

        <div className="mt-6 flex items-center gap-3" data-testid="journal-progress">
          <div className="flex gap-1.5">
            {DAYS.map((d) => (
              <span key={d} className={`h-1.5 w-10 rounded-full ${getEntry(d) ? "bg-[#A68A56]" : "bg-[#E2DFD8]"}`} />
            ))}
          </div>
          <span className="text-xs text-[#718096]">{j.progress(loggedCount)}</span>
          {complete && (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#475F51] ml-2" data-testid="journal-complete-badge">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} /> {j.completeBadge}
            </span>
          )}
        </div>

        {complete && (
          <div className="mt-10 bg-[#0B0E14] text-white p-8 md:p-10" data-testid="journal-measure">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-3">{j.measureOverline}</p>
            <h2 className="font-display text-2xl md:text-3xl font-light">{j.measureTitle}</h2>
            <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-xl">{j.measureBody}</p>
            <div className="mt-8 flex items-end gap-10">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">{j.firstDay}</p>
                <p className="font-display text-4xl font-light tabular-nums">{firstCount}</p>
              </div>
              <TrendingDown className="w-6 h-6 text-[#A68A56] mb-2" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">{j.lastDay}</p>
                <p className="font-display text-4xl font-light tabular-nums">{lastCount}</p>
              </div>
            </div>
            <p className="mt-6 text-sm text-white/70">{j.interventionsLogged(interventions)}</p>
            <div className="mt-6 pt-6 border-t border-white/15">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">{j.metricLabel}</p>
              <p className="font-display text-lg font-normal">{metric}</p>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col gap-4">
          {DAYS.map((d) => (
            <DayCard key={d} day={d} j={j} entry={getEntry(d)} primaryKey={primaryKey} onSave={handleSave} />
          ))}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
